import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cmsAPI, projectsAPI, getImageUrl } from '../utils/api';
import Blob3D from '../components/Blob3D';
import '../styles/pages/Home.scss';

function Home() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    projects: 0,
    clients: 0,
    satisfaction: 0
  });

  const [cmsServices, setCmsServices] = useState([]);
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [heroSection, setHeroSection] = useState({
    title: 'Innovating the Future with Technology',
    subtitle: '← TECHNOLOGY PARTNER',
    description: 'We empower businesses with cutting-edge technology solutions that drive growth, efficiency, and digital transformation.',
    button_text: 'EXPLORE SERVICES →',
    button_link: '/services',
    image: null
  });
  const [loadingServices, setLoadingServices] = useState(true);



  useEffect(() => {
    // Fetch CMS Services
    cmsAPI.getServices()
      .then(res => {
        if (res.data.success) {
          setCmsServices(res.data.data);
        }
        setLoadingServices(false);
      })
      .catch(err => {
        console.error("Error fetching services:", err);
        setLoadingServices(false);
      });

    // Fetch Featured Projects
    projectsAPI.getPublic()
      .then(res => {
        const data = res.data;
        if (data.data) {
          const parsedProjects = data.data.map(p => {
            let parsedImages = [];
            try {
              if (p.images) {
                parsedImages = typeof p.images === 'string' ? JSON.parse(p.images) : p.images;
              }
            } catch (e) {
              console.error("Error parsing images for project", p.id, e);
            }
            return { ...p, images: parsedImages };
          });
          setFeaturedProjects(parsedProjects.slice(0, 4));
        }
      })
      .catch(err => console.error("Error fetching projects:", err));

    // Fetch CMS Home Data
    cmsAPI.getHome()
      .then(res => {
        if (res.data.success && res.data.data) {
          const hero = res.data.data.find(section => section.section_key === 'hero');
          if (hero) {
            setHeroSection({
              title: hero.title || 'Innovating the Future with Technology',
              subtitle: hero.subtitle || '← TECHNOLOGY PARTNER',
              description: hero.description || 'We empower businesses with cutting-edge technology solutions...',
              button_text: hero.button_text || 'EXPLORE SERVICES →',
              button_link: hero.button_link || '/services',
              image: hero.image
            });
          }
        }
      })
      .catch(err => console.error("Error fetching home data:", err));

    // Fetch Testimonials
    cmsAPI.getTestimonials()
      .then(res => {
        const data = res.data;
        if (data.success && data.data) {
          setTestimonials(data.data.slice(0, 3));
        }
      })
      .catch(err => console.error("Error fetching testimonials:", err));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prev => ({
        projects: Math.min(prev.projects + 1, 100),
        clients: Math.min(prev.clients + 1, 50),
        satisfaction: Math.min(prev.satisfaction + 1, 95)
      }));
    }, 20);
    return () => clearInterval(interval);
  }, []);

  const heroBackgroundImage = getImageUrl(heroSection.image, "/images/hero-bg.jpg");

  return (
    <div className="home">
      {/* Hero Section */}
      <section className="hero" style={{ backgroundImage: `url('${heroBackgroundImage}')` }}>
        <Blob3D />
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <span className="hero-tag">{heroSection.subtitle}</span>
          <h1>{heroSection.title}</h1>
          <p>{heroSection.description}</p>
          <div className="hero-buttons">
            <button className="btn btn-primary" onClick={() => navigate(heroSection.button_link)}>{heroSection.button_text}</button>
            <button className="btn btn-outline" onClick={() => navigate('/contact')}>CONTACT US →</button>
          </div>
        </div>
        {/*
        <div className="hero-stats">
          <div className="stat-item">
            <span className="stat-number">{stats.projects}+</span>
            <span className="stat-label">PROJECTS</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{stats.clients}+</span>
            <span className="stat-label">CLIENTS</span>
          </div>
          <div className="stat-item">
            <span className="stat-number">{stats.satisfaction}%</span>
            <span className="stat-label">SATISFACTION</span>
          </div>
        </div>
        */}
      </section>

      {/* Services Section */}
      <section className="services-section">
        <div className="services-container">
          <span className="section-tag">WHAT WE DO</span>
          <h2>Our Services</h2>
          <p className="section-desc">We deliver comprehensive technology solutions tailored to your business needs — from web and mobile to cloud and digital marketing.</p>
          <div className="services-grid">
            {loadingServices ? (
              <p>Loading services...</p>
            ) : cmsServices.length === 0 ? (
              <p>No services found.</p>
            ) : (
              cmsServices.map((service, index) => (
                <div key={service.id} className="service-card">
                  <div className="service-image">
                    <img
                      src={getImageUrl(service.image)}
                      alt={service.title}
                    />
                    <span className="service-num">{String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <div className="service-content">
                    <h3>{service.title}</h3>
                    <p>{service.description}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="who-we-are">
        <div className="who-we-are-content">
          <span className="section-tag">WHO WE ARE</span>
          <h2>Building Technology That Matters</h2>
          <p>We are a team of passionate engineers, designers, and strategists committed to delivering technology solutions that create real impact. Our mission is to empower every business — from ambitious startups to established enterprises — with innovative digital products that drive measurable growth and lasting success.</p>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="featured-projects">
        <div className="projects-container">
          <span className="section-tag">OUR WORK</span>
          <h2>Featured Projects</h2>
          <p className="section-desc">Explore our portfolio of innovative solutions that have helped businesses achieve their digital goals and transform operations.</p>
          <div className="projects-grid">
            {featuredProjects.map((project, index) => {
              const projectImage = project.images && project.images.length > 0 ? project.images[0] : null;
              
              const defaultImages = [
                '/images/port-agritech.jpg',
                '/images/port-dairy.jpg',
                '/images/port-healthcare.jpg',
                '/images/port-iot.jpg'
              ];
              
              const imageUrl = projectImage ? getImageUrl(projectImage) : defaultImages[index % defaultImages.length];

              return (
                <div key={index} className="project-card" onClick={() => navigate(`/premium-preview/${project.id}?source=project`)} style={{ cursor: 'pointer' }}>
                  {imageUrl ? (
                    <img 
                      src={imageUrl} 
                      alt={project.title} 
                      className="project-image" 
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className="project-placeholder" 
                    style={{ 
                      display: imageUrl ? 'none' : 'flex',
                      width: '100%',
                      height: '100%',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'linear-gradient(135deg, #1e293b, #0f172a)',
                      color: '#38bdf8',
                      fontSize: '3rem',
                      fontWeight: '800'
                    }}
                  >
                    <span>{(project.category || project.title || 'P')?.charAt(0)}</span>
                  </div>
                  <div className="project-overlay">
                    <span className="project-category">{project.category}</span>
                    <h3>{project.title}</h3>
                    <p>{project.description_1 ? project.description_1.substring(0, 100) + '...' : ''}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="projects-cta">
            <button className="btn btn-primary btn-lg" onClick={() => { navigate('/projects'); window.scrollTo(0, 0); }}>View All Projects →</button>
          </div>
        </div>
      </section>

      {/* Education & Research Section */}
      <section className="education-section">
        <div className="education-container">
          <span className="section-tag">BEYOND TECHNOLOGY</span>
          <h2>Education & Research</h2>
          <p className="section-desc">We go beyond IT services by empowering students and professionals with quality education, external degree programs, and comprehensive academic documentation support.</p>
          <div className="education-grid">
            <div className="education-card">
              <img src="/images/svc-education.jpg" alt="Education" />
              <span className="education-badge">EDUCATION</span>
              <h3>External Degree Courses</h3>
              <p>Admission guidance for external degree programs including BBA, MBA, B.Lib, M.Lib, BLIS, and MLIS from recognized universities.</p>
            </div>
            <div className="education-card">
              <img src="/images/svc-research.jpg" alt="Research" />
              <span className="education-badge">RESEARCH</span>
              <h3>Documentation & Research</h3>
              <p>Professional synopsis, PPT, research paper publishing via IJSEMI, report writing, and thesis preparation for academic excellence.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="testimonials-container">
          <span className="section-tag">CLIENT FEEDBACK</span>
          <h2>What Our Clients Say</h2>
          <p className="section-desc">Hear directly from the businesses we have partnered with. Their success stories reflect our commitment to excellence and innovation.</p>
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => {
              const avatarUrl = getImageUrl(testimonial.image, `https://ui-avatars.com/api/?name=${encodeURIComponent(testimonial.author)}&background=00526A&color=fff&size=40&bold=true`);

              return (
                <div key={index} className="testimonial-card">
                  <p className="testimonial-quote">"{testimonial.quote}"</p>
                  <div className="testimonial-author">
                    <img src={avatarUrl} alt={testimonial.author} className="author-avatar" />
                    <div className="author-info">
                      <h4>{testimonial.author}</h4>
                      <span>{testimonial.role}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="final-cta" style={{ backgroundImage: "url('/images/cta-bg.jpg')" }}>
        <h2>Ready to Transform Your Business?</h2>
        <p>Let us help you build the technology solutions that will propel your business forward.</p>
        <div className="cta-buttons">
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/contact')}>Start Your Project →</button>
          <button className="btn btn-outline-light btn-lg" onClick={() => navigate('/services')}>Explore Services →</button>
        </div>
      </section>
    </div>
  );
}

export default Home;
